// TenantGate — top-level provider that fires `useTenantFromHost` once at the
// React root and exposes the result to its children via context.
//
// Why a context instead of letting each consumer re-call useTenantFromHost?
// - The TanStack Query cache already dedupes the actual fetch.
// - But a context lets RequireAuth and LoginPage share the SAME pending /
//   resolved / unknown state without each re-rendering on its own micro-state.
// - More importantly, `useMe` lives behind the same gate: TenantGate also
//   eagerly clears any stale cross-tenant session (AC6) BEFORE its children
//   mount, so RequireAuth never has the chance to fire /auth/me with the
//   stale bearer + new tenant.

import {
  createContext,
  useContext,
  useEffect,
  type PropsWithChildren,
  type ReactElement,
} from "react";
import {
  useTenantFromHost,
  type UseTenantFromHostResult,
} from "./useTenantFromHost.js";
import { useAuthStore } from "./store.js";

const TenantContext = createContext<UseTenantFromHostResult | null>(null);

export function TenantGate({ children }: PropsWithChildren): ReactElement {
  const result = useTenantFromHost();

  // AC6: eagerly clear any stale session whose tenant doesn't match the
  // host-resolved tenant. Run synchronously the moment we have a tenant +
  // a session. This must fire BEFORE any protected query mounts —
  // RequireAuth (and useMe) read the same store, so the cleared state is
  // visible by the next render.
  //
  // Compares `tenantSlug` (kebab-case string, identical wire-shape on both
  // sides) rather than `tenantId`. With Zitadel-issued JWTs (BE-016+), the
  // JWT-side `tenantId` carries the Zitadel org snowflake while the
  // tenant-from-host side carries the platform UUID — they never match
  // even for the same tenant. The slug is the durable identifier (it's
  // also the subdomain in the URL) and works across both sides.
  const session = useAuthStore((s) => s.session);
  const markWrongTenant = useAuthStore((s) => s.markWrongTenant);
  useEffect(() => {
    if (
      session &&
      result.tenant &&
      session.tenant.tenantSlug !== result.tenant.tenantSlug
    ) {
      markWrongTenant();
    }
  }, [session, result.tenant, markWrongTenant]);

  return (
    <TenantContext.Provider value={result}>{children}</TenantContext.Provider>
  );
}

export function useTenantContext(): UseTenantFromHostResult {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error("useTenantContext must be used inside <TenantGate>");
  }
  return ctx;
}
